import { ReactElement, Fragment } from "react";
import AxisHorizontal from "../../../../../components/charts/AxisHorizontal";
import { getTicks, useChartDimensions, useUniqueId } from "../../../../../components/charts/chartUtils";
import AxisVertical from "../../../../../components/charts/AxisVertical";
import Chart from "../../../../../components/charts/chart";
import { IPowerOnOffReport } from "../../types";
import * as d3 from "d3";
import { getPalette } from "../../../../../configuration/configuration";
import { formatNumber } from "../../../../../system/utils";
import HtmlTooltip from "../../../../../components/charts/tooltip";

interface IAvgPowerOnOffChartProps {
  data: IPowerOnOffReport[];
}

const paddingInner = 0.1;
const paddingOuter = 0.1;
const align = 0.5;

const CountOfHeatsChart = ({ data }: IAvgPowerOnOffChartProps): ReactElement => {
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: 200,
    marginBottom: 35,
    marginLeft: 60,
    marginRight: 60,
    marginTop: 40
  });

  const chartID = useUniqueId("count-heats-chart");

  const xAccessor = (d: IPowerOnOffReport) => d.factoryDay;
  const yAccessorCount = (d: IPowerOnOffReport) => d.total;
  const yAccessorBillet = (d: IPowerOnOffReport) => d.billetTons;

  let maximumTons = 0;
  data.forEach(x => maximumTons = Math.max(maximumTons, x.billetTons));

  const xScale = d3.scaleBand()
    .domain(data.map(x => `${x.factoryDay}`))
    .range([0, dimensions.boundedWidth])
    .paddingInner(paddingInner)
    .paddingOuter(paddingOuter)
    .align(align);

  const domain = d3.extent(data, yAccessorCount) as number[];
  const yScale = d3.scaleLinear()
    .domain([0, domain[1] ?? 0])
    .range([dimensions.boundedHeight, 0]);

  const yScaleBilletTons = d3.scaleLinear()
    .domain([0, maximumTons])
    .range([dimensions.boundedHeight, 0]);

  const xAccessorScaled = (d: IPowerOnOffReport) => xScale(`${xAccessor(d)}`) ?? 0;
  const yAccessorScaled = (d: IPowerOnOffReport) => yScaleBilletTons(yAccessorBillet(d));

  const lineGenerator = d3.line<IPowerOnOffReport>()
    .x(xAccessorScaled)
    .y(yAccessorScaled)
    .curve(d3.curveLinear);

  const lineOffset = xScale.bandwidth() / 2.0;

  return(
    <div ref={ref} style={{ justifySelf: "stretch", alignSelf: "stretch" }}>
      <Chart dimensions={dimensions} >
        <AxisHorizontal
          chartID={chartID}
          dimensions={dimensions}
          scale={xScale}
          label="Day"
          ticks={data.map(item => `${item.factoryDay}`)}
          formatTick={ (_value, index) => `${data[index].day}`}
        />

        <AxisVertical
          chartID={`${chartID}-count`}
          dimensions={dimensions}
          scale={yScale}
          label="Count of Heats"
          ticks={getTicks(yScale.domain(), 5)}
          side="LEFT"
          formatTick={(value) => (value as number).toFixed(0)}/>

        <AxisVertical
          chartID={`${chartID}-billets`}
          dimensions={dimensions}
          scale={yScaleBilletTons}
          label="Billet Tons"
          ticks={getTicks(yScaleBilletTons.domain(), 3)}
          side="RIGHT"
          formatTick={(value) => (value as number).toFixed(0)}/>

        <g>
          <circle r={5} fill={getPalette().statusColor.gray.background} cy={-15} cx={6} strokeWidth={0}/>
          <text y={-10} x={15} >Count heats</text>

          <circle r={5} fill={getPalette().statusColor.warning.background} cy={-15} cx={126} strokeWidth={0}/>
          <text y={-10} x={135} >Billet Tons</text>
        </g>

        <g>
          {data && data.map((item, index) => {
            const y = yScale(yAccessorCount(item)) ?? 0;
            const yLine = yScaleBilletTons(yAccessorBillet(item)) ?? 0;

            const x = xScale(`${xAccessor(item)}`) ?? 0;
            const width = xScale.bandwidth();
            const height = dimensions.boundedHeight - y;

            return (
              <Fragment key={`${chartID}-${index}`}>
                <HtmlTooltip title={
                  <div>
                    <div className="display-flex-row align-items-center" >
                      <div style={{ background: getPalette().statusColor.gray.background, width: 10, height: 10, marginRight: 10 }} />
                      <strong className="flex-grow-1">Count heats</strong>
                      <strong className="flex-grow-0">{formatNumber(yAccessorCount(item), 1)}</strong>
                    </div>
                  </div>}>
                  <rect
                    className="Bars__rect"
                    fill={getPalette().statusColor.gray.background}
                    height={height}
                    width={width}
                    y={y}
                    x={x}
                  />
                </HtmlTooltip>

                <HtmlTooltip title={
                  <div>
                    <div className="display-flex-row align-items-center" >
                      <div style={{ background: getPalette().statusColor.warning.background, width: 10, height: 10, marginRight: 10 }} />
                      <strong className="flex-grow-1">Billet Tons</strong>
                      <strong className="flex-grow-0">{formatNumber(yAccessorBillet(item), 1)}</strong>
                    </div>
                  </div>}>
                  <circle
                    className="Bars__rect"
                    fill={getPalette().statusColor.warning.background}
                    r={4}
                    cy={yLine}
                    cx={x + lineOffset}
                  />
                </HtmlTooltip>
              </Fragment>
            );
          })
          }
        </g>

        <path
          transform={`translate( ${lineOffset}, 0)`}
          className={"Line"}
          fill="none"
          stroke={getPalette().statusColor.warning.background}
          strokeWidth={3}
          d={lineGenerator(data) ?? ""}
        />

      </Chart>
    </div>
  );
};

export default CountOfHeatsChart;
